import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Brush,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import "./DriversDifferenceFromAverage.css";
import { useGetDriversBreakdownMutation } from "../../../store/api/lumi/drivers";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { getType } from "../../../utils/date";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { roundNumber } from "../../../utils/roundNumber";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getLineColor } from "../../../utils/getLineColor";
import { Colors } from "../../../constants/colors";

export function DriversDifferenceFromAverage(props) {
    const [chartData, setChartData] = useState([]);
    const [getDriversBreakdown] = useGetDriversBreakdownMutation();
    const [getVolumes] = useGetConceptsFiltersMutation();
    const [loading, setLoading] = useState(true);
    const [tooltipData, setTooltipData] = useState({});
    const [maxValue, setMaxValue] = useState(0);
    const [conceptColors, setConceptColors] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });


    const {
        id,
        selectedFieldDriverName,
        filters,
        seletctedType,
        selectedDateName,
        interval,
        setFilterDocCount,
        smoothLine
    } = props;

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props]
    );

    const getConcepts = useCallback(async () => {
        const { data: dataConcepts } = await getVolumes({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
        });
        setFilterDocCount(dataConcepts.filter_count);
    }, [conceptFilter, filters, getVolumes, id, setFilterDocCount]);

    const getDrivers = useCallback(async () => {
        setLoading(true);
        const { data } = await getDriversBreakdown({
            id,
            concept_selector: JSON.parse(conceptFilter),
            score_field: selectedFieldDriverName,
            filter: filters,
            match_type: getType(seletctedType),
            breakdowns: [{ name: selectedDateName, interval }],
        });

        const breakdownData = data[0].buckets;
        const conceptsMap = {};
        const conceptColors = {};
        const additionData = {};
        let maximalValue = 0;

        breakdownData.forEach(({ label, drivers }) => {
            const formattedDate = formatDateToUtc(label, props.interval);
            drivers.forEach(({ name, average_score, baseline, color }) => {
                if (!conceptsMap[name]) {
                    conceptsMap[name] = [];
                    if (color) conceptColors[name] = color;
                }

                let count = roundNumber((average_score / baseline) * 100);

                if (baseline === 0) {
                    return;
                    // count = average_score ? average_score : 100;
                }

                maximalValue = Math.max(maximalValue, count);

                additionData[`${formattedDate}-${name}`] = {
                    average_score: roundNumber(average_score),
                    count,
                };

                if (!additionData[formattedDate]) {
                    additionData[formattedDate] = roundNumber(baseline);
                }

                conceptsMap[name].push({
                    date: formattedDate,
                    [name]: count,
                });
            });
        });
        setConceptColors(conceptColors);
        const aggregatedData = Object.values(conceptsMap)
            .flat()
            .reduce((acc, curr) => {
                if (acc.has(curr.date)) {
                    acc.set(curr.date, { ...acc.get(curr.date), ...curr });
                } else {
                    acc.set(curr.date, curr);
                }
                return acc;
            }, new Map())
            .values();

        const aggregatedDataArray = Array.from(aggregatedData);

        setTooltipData(additionData);
        setChartData(aggregatedDataArray);
        setLoading(false);
        setMaxValue(maximalValue);
    }, [
        getDriversBreakdown,
        id,
        conceptFilter,
        selectedFieldDriverName,
        filters,
        seletctedType,
        selectedDateName,
        interval,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetDrivers = debounce(getDrivers, 1000);

        debouncedGetDrivers();

        return () => {
            debouncedGetDrivers.cancel();
        };
    }, [getDrivers]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetConcepts = debounce(getConcepts, 1000);

        debouncedGetConcepts();

        return () => {
            debouncedGetConcepts.cancel();
        };
    }, [getConcepts]);

    const renderTooltipContent = (o) => {
        const { payload, label } = o;

        return (
            <div className="custom-tooltip">
                <p className="label">
                    {`${label}`}
                    <br />
                    Overall average score: <b>{`${tooltipData[label]}`}</b>
                </p>
                {payload?.map((entry, index) => {
                    const { average_score, count } =
                        tooltipData[`${label}-${entry.name}`] || {};

                    return (
                        <p
                            key={`${label}-${entry.name}-${index}`}
                            style={{ color: entry.color, fontSize: "14px" }}
                        >
                            {`${entry.name} : ${average_score} (${count}%)`}
                        </p>
                    );
                })}
            </div>
        );
    };
    const [minValue, setMinValue] = useState(0);

    useEffect(() => {
        if (chartData.length > 0) {
            const localMax = Math.max(...chartData.map(d => Math.max(...Object.values(d).filter(val => typeof val === 'number'))));
            const localMin = Math.min(...chartData.map(d => Math.min(...Object.values(d).filter(val => typeof val === 'number'))));

            setMaxValue(localMax);
            setMinValue(localMin);
        }
    }, [chartData]);
    if (loading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    return (
        <div className="volume-compare"  style={{height: props.widget.layout.h * 39 - 150}}
             onMouseDown={(e) => {
                     e.stopPropagation();
                     e.preventDefault()
             }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 30, right: 20, left: 50, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }} />
                    <YAxis
                        label={{
                            angle: -90,
                            position: "insideLeft",
                            offset: 0,
                            style: { textAnchor: "middle", fontSize: 16, color: "#000", fontWeight: "bold" },
                        }}
                        tickFormatter={(tick) =>
                            `${new Intl.NumberFormat("en", { notation: "compact" }).format(tick)}%`
                        }
                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                        domain={[minValue, maxValue + maxValue * 0.05]}
                    />
                    <ReferenceLine
                        y={100}
                        stroke="black"
                        strokeDasharray="5 5"
                        strokeWidth="2"
                        ifOverflow="extendDomain"
                        label={{
                            value: "Baseline",
                            position: 'left',
                            top: 0,
                            fill: "#000",
                            fontSize: 16,
                            style: {
                                fill: '#000',
                                fontWeight: 'bold',
                                width: '200px',
                                textAlign: 'center'
                            }
                        }}
                        xAxisId="0"
                    />

                    <Tooltip content={renderTooltipContent} />
                    <Legend
                        wrapperStyle={{
                            fontSize: `${labelFontSize}px`,
                            fontWeight: "bold",
                            color: "#000",
                        }}
                    />
                    <defs>
                        <linearGradient id="brushGreyGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#B0B0B0" stopOpacity={0.7} />
                            <stop offset="50%" stopColor="#D3D3D3" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#F5F5F5" stopOpacity={0.3} />
                        </linearGradient>
                    </defs>
                    <Brush
                        dataKey="date"
                        height={35}
                        stroke={Colors.gray7}
                        fill="url(#brushGreyGradient)"
                        travellerWidth={28}
                        travellerStyle={{
                            stroke: "#555",
                            fill: "#777",
                            cursor: "pointer",
                        }}
                        selectedBoxStyle={{
                            stroke: "#555",
                            fill: "rgba(175,168,168,0.3)",
                            cursor: "move",
                        }}
                        gap={1}
                    />
                    {Object.keys(chartData[0] ?? {})
                        .filter((key) => key !== "date")
                        .map((key, idx) => (
                            <Line
                                strokeWidth={2}
                                dot={!smoothLine}
                                type={smoothLine ? "monotone" : "linear"}
                                dataKey={key}
                                stroke={conceptColors[key] || getLineColor(idx)}
                                key={key}
                            />
                        ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
